import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../constants/main';
import { LANGUAGE_NAMES, SUPPORTED_LOCALES } from '../i18n/languages';
import { sortLanguagesByLanguage } from '../i18n/sortLanguages';

export interface ActiveLanguage {
  locale: string;
  label: string;
  isDefault?: boolean;
}

// Used if the API is unreachable so pickers never render empty.
const FALLBACK: ActiveLanguage[] = sortLanguagesByLanguage(
  SUPPORTED_LOCALES.map((locale) => ({
    locale,
    label: LANGUAGE_NAMES[locale],
  })),
);

// Fetched once, then shared for the session.
let cache: ActiveLanguage[] | null = null;

export async function fetchActiveLanguages(): Promise<ActiveLanguage[]> {
  if (cache) return cache;
  let result: ActiveLanguage[] = FALLBACK;
  try {
    const res = await fetch(`${API_BASE_URL}/api/languages`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (data?.languages?.length) {
      result = sortLanguagesByLanguage(data.languages);
    }
  } catch {
    // keep fallback
  }
  cache = result;
  return result;
}

// DB-driven active languages for user-facing pickers, with a static fallback.
export function useActiveLanguages(): ActiveLanguage[] {
  const { i18n } = useTranslation();
  const [languages, setLanguages] = useState<ActiveLanguage[]>(cache ?? FALLBACK);
  useEffect(() => {
    fetchActiveLanguages().then(setLanguages);
  }, []);
  return useMemo(
    () => sortLanguagesByLanguage(languages, i18n.language),
    [languages, i18n.language],
  );
}
